import type { ContractMode } from "./watchTypes";

export type DteScheduleReachability = {
  reachable: boolean;
  reason: "ALREADY_PASSED" | "AFTER_MONITORING_WINDOW" | null;
  daysUntilEligible: number | null;
  suggestedDuration: number | null;
};

/**
 * Compare a held option's DTE condition with the customer-selected calendar
 * monitoring window. DTE itself remains server evaluated; this helper only
 * prevents authoring a calendar window that ends before that condition can
 * first become true.
 */
export function getDteScheduleReachability(params: {
  currentDte: number;
  threshold: number;
  operator: "lte" | "eq";
  duration: number;
  supportedDurations?: readonly number[];
}): DteScheduleReachability {
  const currentDte = Math.max(0, Math.floor(params.currentDte));
  const threshold = Math.max(0, Math.floor(params.threshold));
  const duration = Math.max(0, Math.floor(params.duration));
  const supportedDurations = [...(params.supportedDurations ?? [1, 5, 10, 30])]
    .filter((value) => Number.isInteger(value) && value > 0)
    .sort((a, b) => a - b);

  if (params.operator === "eq" && threshold > currentDte) {
    return {
      reachable: false,
      reason: "ALREADY_PASSED",
      daysUntilEligible: null,
      suggestedDuration: null,
    };
  }

  const daysUntilEligible = Math.max(0, currentDte - threshold);
  if (daysUntilEligible <= duration) {
    return { reachable: true, reason: null, daysUntilEligible, suggestedDuration: null };
  }

  return {
    reachable: false,
    reason: "AFTER_MONITORING_WINDOW",
    daysUntilEligible,
    suggestedDuration: supportedDurations.find((value) => value >= daysUntilEligible) ?? null,
  };
}

export function addDays(base: Date, days: number) {
  const result = new Date(base);
  result.setDate(result.getDate() + Math.max(days, 0));
  return result;
}

export function formatShortDate(value: Date) {
  return value.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

export function getContractExpiryInfo(params: {
  contractMode: ContractMode;
  contractExpiry: string;
  manualOCC: string;
  today?: Date;
}) {
  const today = params.today ? new Date(params.today) : new Date();
  today.setHours(0, 0, 0, 0);

  let expiryDate: Date | null = null;
  if (params.contractMode === "builder" && params.contractExpiry) {
    const expireParts = params.contractExpiry.split("-");
    if (expireParts.length === 3) {
      expiryDate = new Date(
        parseInt(expireParts[0], 10),
        parseInt(expireParts[1], 10) - 1,
        parseInt(expireParts[2], 10),
      );
    }
  } else if (params.contractMode === "manual" && params.manualOCC) {
    const match = params.manualOCC.trim().toUpperCase().match(/(\d{6})[CP]\d{8}$/);
    if (match) {
      const dateStr = match[1];
      const yy = parseInt(dateStr.slice(0, 2), 10);
      const mm = parseInt(dateStr.slice(2, 4), 10) - 1;
      const dd = parseInt(dateStr.slice(4, 6), 10);
      expiryDate = new Date(2000 + yy, mm, dd);
    }
  }

  if (!expiryDate) return null;
  const dte = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  // An expired contract is not a one-day monitoring window. Returning null
  // prevents the authoring UI from presenting a past expiry as actionable.
  if (dte < 0) return null;
  return {
    date: expiryDate,
    dte: Math.max(1, dte),
  };
}

export function getMonitoringWindowState(params: {
  duration: number;
  contractExpiryInfo: { date: Date; dte: number } | null;
  today?: Date;
}) {
  const today = params.today ? new Date(params.today) : new Date();
  today.setHours(0, 0, 0, 0);

  let displayDuration = params.duration;
  if (params.contractExpiryInfo && displayDuration > params.contractExpiryInfo.dte) {
    displayDuration = params.contractExpiryInfo.dte;
  }

  const monitoringWindowOptions = (() => {
    const raw = params.contractExpiryInfo
      ? [
          {
            key: "expiry",
            label: `Through ${formatShortDate(params.contractExpiryInfo.date)} expiry`,
            helper: `${params.contractExpiryInfo.dte} DTE`,
            days: params.contractExpiryInfo.dte,
          },
          {
            key: "5",
            label: "Next 5 days",
            helper: `through ${formatShortDate(addDays(today, 5))}`,
            days: 5,
          },
          {
            key: "14",
            label: "Next 14 days",
            helper: `through ${formatShortDate(addDays(today, 14))}`,
            days: 14,
          },
          {
            key: "30",
            label: "Next 30 days",
            helper: `through ${formatShortDate(addDays(today, 30))}`,
            days: 30,
          },
        ]
      : [
          {
            key: "5",
            label: "Next 5 days",
            helper: `through ${formatShortDate(addDays(today, 5))}`,
            days: 5,
          },
          {
            key: "14",
            label: "Next 14 days",
            helper: `through ${formatShortDate(addDays(today, 14))}`,
            days: 14,
          },
          {
            key: "30",
            label: "Next 30 days",
            helper: `through ${formatShortDate(addDays(today, 30))}`,
            days: 30,
          },
        ];

    return raw
      .filter(
        (option, index, array) =>
          index === array.findIndex((candidate) => candidate.days === option.days),
      )
      .slice(0, 3);
  })();

  const presetWindowDays = monitoringWindowOptions.map((option) => option.days);
  const isCustomWindowSelected = !presetWindowDays.includes(params.duration);
  const suggestedCustomWindowDays = (() => {
    const preferred = params.contractExpiryInfo
      ? Math.max(1, Math.min(10, params.contractExpiryInfo.dte))
      : 10;

    if (!presetWindowDays.includes(preferred)) return preferred;

    for (let delta = 1; delta <= 30; delta += 1) {
      for (const candidate of [preferred + delta, preferred - delta]) {
        if (candidate >= 1 && candidate <= 30 && !presetWindowDays.includes(candidate)) {
          return candidate;
        }
      }
    }

    return preferred;
  })();

  const customWindowEndDate = addDays(today, params.duration);
  const monitoringWindowSummary = (() => {
    if (
      isCustomWindowSelected &&
      params.contractExpiryInfo &&
      displayDuration < params.duration
    ) {
      return `Custom ${params.duration}-day window, capped at ${formatShortDate(params.contractExpiryInfo.date)} expiry (${displayDuration} DTE)`;
    }
    if (isCustomWindowSelected) {
      return `Custom window through ${formatShortDate(customWindowEndDate)} (${displayDuration} ${displayDuration === 1 ? "day" : "days"})`;
    }
    if (params.contractExpiryInfo && displayDuration < params.duration) {
      return `Through ${formatShortDate(params.contractExpiryInfo.date)} expiry (${displayDuration} DTE, capped)`;
    }
    if (params.contractExpiryInfo && params.duration === params.contractExpiryInfo.dte) {
      return `Through ${formatShortDate(params.contractExpiryInfo.date)} expiry (${displayDuration} DTE)`;
    }
    return `Through ${formatShortDate(addDays(today, displayDuration))} (${displayDuration} ${displayDuration === 1 ? "day" : "days"})`;
  })();

  return {
    displayDuration,
    monitoringWindowOptions,
    presetWindowDays,
    isCustomWindowSelected,
    suggestedCustomWindowDays,
    customWindowEndDate,
    monitoringWindowSummary,
    isMonitoringWindowActive: (days: number) => displayDuration === days,
  };
}
